import { useState } from "react";

import { Button } from "components/ui/Button";
import { DatePickerField } from "components/ui/DatePickerField/DatePickerField";
import { Select } from "components/ui/Select/Select";
import { WeekDayCircles } from "components/ui/WeekDayCircles";

import { emptyAvailability } from "../../hooks/useStaffAvailability";
import {
  selectedDayIds,
  staffOptionLabel,
} from "../../utils/availabilityFormat";
import {
  SINGLE_DATE_HINT,
  toggleDay,
} from "../../utils/availabilityRecurrence";
import styles from "./StaffAvailabilitySection.module.css";
import { TimeField } from "./TimeField";

export const AvailabilityForm = ({ staffMembers, isSaving, onSave }) => {
  const [record, setRecord] = useState(emptyAvailability);

  const update = (changes) => setRecord((prev) => ({ ...prev, ...changes }));

  const handleSave = async () => {
    const saved = await onSave(record);
    if (saved) setRecord(emptyAvailability());
  };

  const staffOptions = staffMembers.map((staff) => ({
    value: String(staff.id),
    label: staffOptionLabel(staff),
  }));

  return (
    <div className={styles.form}>
      <div className={styles.formGrid}>
        <div className={styles.field}>
          <label className={styles.label}>Staff Member</label>
          <Select
            options={staffOptions}
            value={record.staffMemberId ? String(record.staffMemberId) : ""}
            onChange={(value) => update({ staffMemberId: value || null })}
            placeholder="Select staff member..."
            fullWidth
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="availability-start-time">
            Start Time
          </label>
          <TimeField
            id="availability-start-time"
            className={styles.textInput}
            value={record.startTime}
            onChange={(event) => update({ startTime: event.target.value })}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="availability-end-time">
            End Time
          </label>
          <TimeField
            id="availability-end-time"
            className={styles.textInput}
            value={record.endTime}
            onChange={(event) => update({ endTime: event.target.value })}
          />
        </div>

        <div className={styles.field}>
          {/* A one-off has a single date, so "Start" would be a date with
              nothing to start. */}
          <label className={styles.label}>
            {record.recurring ? "Start Date" : "Date"}
          </label>
          <DatePickerField
            value={record.availableStartDate}
            onChange={(value) => update({ availableStartDate: value })}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label}>End Date</label>
          <DatePickerField
            value={record.recurring ? record.availableEndDate : ""}
            onChange={(value) => update({ availableEndDate: value })}
            disabled={!record.recurring}
            // A one-off has no end date, so the field stays blank rather than
            // inviting a click. CustomCalendar falls back to "Select" on an
            // empty string, hence the space.
            placeholder={record.recurring ? "Select date" : " "}
          />
        </div>

        {/* The days only mean anything while Recurring is on, so the two sit
            in one column side by side. */}
        <div className={styles.inlineGroup}>
          <div className={styles.field}>
            <label className={styles.label}>Available Days</label>
            <div className={styles.switchRow}>
              <WeekDayCircles
                multiple
                value={selectedDayIds(record)}
                onChange={(day) => update(toggleDay(record, day))}
                disabled={!record.recurring}
                title={record.recurring ? undefined : SINGLE_DATE_HINT}
              />
            </div>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Recurring</label>
            <div className={styles.switchRow}>
              <div className="form-check form-switch onoff_area">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  aria-label="Recurring"
                  checked={record.recurring}
                  onChange={(event) =>
                    update({ recurring: event.target.checked })
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.formFooter}>
        <Button
          onClick={handleSave}
          disabled={isSaving || !record.staffMemberId}
        >
          {isSaving ? "Saving..." : "Add Availability"}
        </Button>
      </div>
    </div>
  );
};
